import {z} from 'zod';

import type {CourseDocument} from '../firebase/documents/course';

/**
 * How often an active student's browser refreshes their course presence
 * record. The online window ({@link COURSE_PRESENCE_ONLINE_WINDOW_MS}) must
 * stay several times longer than this, or continuously active students drop
 * out of the count between check-ins.
 */
export const COURSE_PRESENCE_CHECK_IN_INTERVAL_MS = 5 * 60 * 1000;

/** How far back a check-in still counts the student as online. */
export const COURSE_PRESENCE_ONLINE_WINDOW_MS = 15 * 60 * 1000;

/**
 * Field on each presence record holding the server time of the student's last
 * check-in. The Realtime Database rules index this field and require every
 * write to set it to the server's `now`.
 */
export const COURSE_PRESENCE_LAST_ACTIVE_FIELD = 'lastActive';

/**
 * One student's presence record at {@link getCoursePresenceUserPath}. Written
 * only by that student.
 */
export const CoursePresenceRecordSchema = z.object({
  /** Server time, in epoch milliseconds, of the student's last check-in. */
  [COURSE_PRESENCE_LAST_ACTIVE_FIELD]: z.number(),
});

/** One student's course presence record. */
export type CoursePresenceRecord = z.infer<typeof CoursePresenceRecordSchema>;

/**
 * Per-course aggregate at {@link getCoursePresenceSummaryPath}, written only
 * by the server and readable by any signed-in user.
 */
export const CoursePresenceSummarySchema = z.object({
  /** Students with a check-in inside the online window. */
  numOnline: z.number().int().nonnegative(),
  /** Server time, in epoch milliseconds, when the summary was computed. */
  updatedAt: z.number(),
});

/** Per-course presence aggregate. */
export type CoursePresenceSummary = z.infer<typeof CoursePresenceSummarySchema>;

/**
 * Realtime Database path of every student's presence record in a course.
 *
 * @param args.courseId - The course.
 * @return The parent path of the per-student records.
 * @throws Never.
 */
export function getCoursePresenceUsersPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `presence/courses/${courseId}/users`;
}

/**
 * Realtime Database path of one student's presence record in a course.
 *
 * @param args.courseId - The course.
 * @param args.uid - The student.
 * @return The record path.
 * @throws Never.
 */
export function getCoursePresenceUserPath({
  courseId,
  uid,
}: {
  courseId: string;
  uid: string;
}): string {
  return `${getCoursePresenceUsersPath({courseId})}/${uid}`;
}

/**
 * Realtime Database path of a course's presence summary.
 *
 * @param args.courseId - The course.
 * @return The summary path.
 * @throws Never.
 */
export function getCoursePresenceSummaryPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `presence/courses/${courseId}/summary`;
}

/** Course document fields that decide what the presence system does. */
export type CoursePresenceSettings = Pick<
  CourseDocument,
  'coursePresenceEnabled' | 'onlineCountEnabled'
>;

/**
 * Whether a course shows the online count from the new presence system. Both
 * the course's opt-in to the new system and its online-count toggle must be
 * on; a course that has not opted in keeps the legacy count.
 *
 * @param args.course - The course document, or null/undefined while it loads,
 *   which returns false.
 * @return Whether the course runs the new online count.
 * @throws Never.
 */
export function isCoursePresenceOnlineCountOn({
  course,
}: {
  course: CoursePresenceSettings | null | undefined;
}): boolean {
  return (
    course?.coursePresenceEnabled === true && course.onlineCountEnabled === true
  );
}

/**
 * How often a student's browser should check in for this course, given the
 * features the course has turned on. Every enabled feature reads the same
 * record, so the interval is that of the most demanding one.
 *
 * @param args.course - The course document, or null/undefined while it loads,
 *   which returns null.
 * @return The check-in interval in milliseconds, or null when no enabled
 *   feature needs presence and the browser should not write at all.
 * @throws Never.
 */
export function getCoursePresenceCheckInIntervalMs({
  course,
}: {
  course: CoursePresenceSettings | null | undefined;
}): number | null {
  if (!isCoursePresenceOnlineCountOn({course})) {
    return null;
  }
  return COURSE_PRESENCE_CHECK_IN_INTERVAL_MS;
}

/**
 * Whether a browser is due to check in again. Due when it has never checked
 * in, or when at least `intervalMs` has passed since the last check-in: the
 * quiet period is the half-open interval `[lastCheckInMs, lastCheckInMs +
 * intervalMs)`.
 *
 * @param args.lastCheckInMs - Local time of the last check-in, or null when
 *   there has been none in this session.
 * @param args.nowMs - Local time now.
 * @param args.intervalMs - The check-in interval.
 * @return Whether to check in now.
 * @throws Never.
 */
export function isCoursePresenceCheckInDue({
  lastCheckInMs,
  nowMs,
  intervalMs,
}: {
  lastCheckInMs: number | null;
  nowMs: number;
  intervalMs: number;
}): boolean {
  return lastCheckInMs === null || nowMs - lastCheckInMs >= intervalMs;
}

/**
 * Earliest check-in time that still counts as online. A student is online when
 * their last check-in falls in the closed interval `[returned value, nowMs]`.
 *
 * @param args.nowMs - Server time now, in epoch milliseconds.
 * @return The window's inclusive lower bound, in epoch milliseconds.
 * @throws Never.
 */
export function getCoursePresenceOnlineWindowStartMs({
  nowMs,
}: {
  nowMs: number;
}): number {
  return nowMs - COURSE_PRESENCE_ONLINE_WINDOW_MS;
}
